import React, { useEffect } from 'react';
import {
  Square,
  Button,
  SvgX,
  Heading,
  Image,
  Stack,
  Flex,
  Copy,
  Link,
} from '@grubhubprod/gh-cookbook-react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { Modal } from 'src/components/Modal/Modal';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { getConfig } from 'src/utilities/config';
import { getWindow } from 'src/utilities/window';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

interface ICongratulatoryModalProps {
  onClose: () => void;
}

interface ICongratulatoryRow {
  image: string;
  header: string;
  body: string | JSX.Element;
}

const CONGRATULATORY_ROWS: ICongratulatoryRow[] = [
  {
    image:
      'https://media-cdn.grubhub.com/grubhub-assets/image/upload/v1772190855/corp-management-ui/balance/Grubhub%20Wallet%20Money%20Froth%201.png',
    header: 'Check credit limit and manage payments',
    body: 'Your corporate credit limit is approved by Balance for simplified monthly invoicing. Manage all payments, invoices, and credit usage in the secure Balance portal.',
  },
  {
    image:
      'https://media-cdn.grubhub.com/grubhub-assets/image/upload/v1772190855/corp-management-ui/balance/Building%201A%20Illustration%201.png',
    header: 'Invite others to use meal credits',
    body: 'Your line of credit will automatically be applied to your people’s accounts the day it’s active, and they will receive an email letting them know they can start ordering.',
  },
  {
    image:
      'https://media-cdn.grubhub.com/grubhub-assets/image/upload/v1772190855/corp-management-ui/balance/Grubhub%20Phone%20Illustration.png',
    header: 'Manage your account',
    body: (
      <>
        Bookmark this page or access{' '}
        <Link
          href={get(getWindow(), 'location', 'origin')}
          target="_blank"
          rel="noreferrer noopener"
        >
          Grubhub
        </Link>{' '}
        and click on your name to access your business account.
      </>
    ),
  },
];

export function CongratulatoryModal({
  onClose,
}: ICongratulatoryModalProps): JSX.Element {
  const { user } = useAuth();
  const { selfserviceFeedbackLink } = getConfig();
  const firstName = get(user, 'first_name') || '';

  const { sendTealiumPageView } = useAnalytics();
  const configuration = getConfig();

  useEffect(() => {
    // send analytics
    sendTealiumPageView({
      pageGroup: 'corporate self service - sign up flow',
      pageName: 'signup completed',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal size="sm">
      <Square as="header" width="100%" inset="sm">
        <Button
          variant="tertiary"
          type="button"
          onClick={onClose}
          className="removeButtonPadding"
        >
          <SvgX size="24px" aria-label="Close modal" />
        </Button>
      </Square>

      <Square width="100%" inset="md">
        <Heading variant="h2">
          {firstName}, you're all set. Here's how to:
        </Heading>
      </Square>

      <Square width="100%" inset="md">
        <Stack y="md">
          {CONGRATULATORY_ROWS.map(({ header, image, body }) => {
            return (
              <Stack key={header} y="md">
                <Flex>
                  <Stack
                    x="md"
                    style={{
                      flexShrink: 0,
                    }}
                  >
                    <Image src={image} alt="" role="presentation" width={70} />
                  </Stack>
                  <div>
                    <Stack y="xxx-sm">
                      <Heading variant="h6">{header}</Heading>
                    </Stack>
                    <Copy variant="body">{body}</Copy>
                  </div>
                </Flex>
              </Stack>
            );
          })}
        </Stack>
        <Copy variant="body">
          How was your experience? Help us make it even better.{' '}
          <Link
            href={selfserviceFeedbackLink}
            target="_blank"
            rel="noreferrer noopener"
          >
            Leave feedback
          </Link>
        </Copy>
      </Square>

      <Square as="footer" width="100%" inset="md">
        <Flex alignItems="center" justifyContent="end">
          <Stack
            x="x-sm"
            style={{ maxWidth: '176px', width: '100%' }}
            display="flex"
            justifyContent="center"
          >
            <Link
              href={configuration.faqURL}
              target="_blank"
              rel="noreferrer noopener"
              style={{ textAlign: 'center' }}
            >
              See FAQs
            </Link>
          </Stack>

          <Stack style={{ maxWidth: '176px', width: '100%' }}>
            <Button
              variant="primary"
              buttonSize="medium"
              block
              onClick={onClose}
            >
              Got it
            </Button>
          </Stack>
        </Flex>
      </Square>
    </Modal>
  );
}
